import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Image } from '@fluentui/react-components';
import classNames from 'classnames';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { getRoutePathById, allSiteMap } from '@bingads-webui-react/with-site-map';

import { TIP_ID } from './consts';

import createCampaignImage from '../media/create-campaign.png';
import promoImage from '../media/promo.png';
import paymentImage from '../media/payment.png';
import coachingImage from '../media/coaching.png';
import helpImage from '../media/help.png';

function getCreateCampaignText({ i18n, hasCoupon, scope }) {
  const href = `${getRoutePathById(allSiteMap, 'import-google')}?${scope.stringify()}`;
  const template = hasCoupon ? _TL_('Reach millions on the Microsoft Advertising Network. Start now by building your first campaign or {{importLink}}importing them directly from Google Ads{{/importLink}}. Once you do, you\'ll unlock our promotional offer for new customers!') : _TL_('Reach millions on the Microsoft Advertising Network. Start now by building your first campaign or {{importLink}}importing them directly from Google Ads{{/importLink}}.');

  return (
    <ReactTemplate
      model={{
        importLink: text => (<Link to={href} data-analyticsid="welcome-import">{text}</Link>),
      }}
      template={i18n.getString(template)}
    />
  );
}

export const useTipsConfig = ({
  showWelcomeMsg, isPaymentFirstTile, hasCoupon, name, i18n, styles, scope,
}) => useMemo(() => ({
  [TIP_ID.PROMO]: {
    image: <Image alt={i18n.getString(_TL_('Welcome to Microsoft Advertising'))} fit="contain" src={promoImage} className={classNames('promo-image', styles.image)} />,
    title: showWelcomeMsg ? i18n.getString(_TL_('Welcome to Microsoft Advertising, {{name}}!'), { name }) : i18n.getString(_TL_('{{name}}, you\'re all set!'), { name }),
    text: i18n.getString(_TL_('Thanks for setting up your first campaign and your payment method! You\'ve unlocked your promotional offer and are now closer to advertising credits. Visit the promotions page to see the status.')),
  },
  [TIP_ID.CREATE_CAMPAIGN]: {
    image: <Image alt={i18n.getString(_TL_('Welcome to Microsoft Advertising'))} fit="contain" src={createCampaignImage} className={classNames('create-campaign-image', styles.image)} />,
    title: i18n.getString(_TL_('Welcome to Microsoft Advertising, {{name}}!'), { name }),
    text: getCreateCampaignText({ i18n, hasCoupon, scope }),
  },
  [TIP_ID.ADD_PAYMENT]: {
    image: <Image alt={i18n.getString(_TL_('Add payment'))} fit="contain" src={paymentImage} className={classNames('add-payment-image', styles.image)} />,
    title: showWelcomeMsg && isPaymentFirstTile ? i18n.getString(_TL_('Welcome to Microsoft Advertising, {{name}}!'), { name }) : i18n.getString(_TL_('Add a payment method')),
    text: i18n.getString(hasCoupon && isPaymentFirstTile ? _TL_('Be seen by millions on the Microsoft Advertising Network. But first, you\'ll need to add a payment method for your advertising charges. Once you do, you\'ll unlock our promotional offer for new customers!') : _TL_('Be seen by millions on the Microsoft Advertising Network. But first, you\'ll need to add a payment method for your advertising charges.')),
  },
  [TIP_ID.COACHING]: {
    image: <Image alt={i18n.getString(_TL_('Coaching'))} fit="contain" src={coachingImage} className={classNames('coaching-image', styles.image)} />,
    title: i18n.getString(_TL_('In it to win it')),
    text: i18n.getString(_TL_('Master the marketplace with a Microsoft Advertising coach who will help you plan, create, and improve your ad campaigns.')),
  },
  [TIP_ID.HELP]: {
    image: <Image alt={i18n.getString(_TL_('Help'))} fit="contain" src={helpImage} className={classNames('help-image', styles.image)} />,
    title: i18n.getString(_TL_('Need a lifeline?')),
    text: i18n.getString(_TL_('Find useful info in a flash on Microsoft Advertising Help. Support agents are also a call, a chat, and a tweet away to serve you.')),
  },
}), [showWelcomeMsg, hasCoupon, i18n, isPaymentFirstTile, name, styles]);
